import { json2csv } from 'json-2-csv';
import type { Table, Row } from '@tanstack/react-table';

interface ExportOptions {
  filename?: (data: any[]) => string;
  transform?: (data: any[]) => any[];
}

function flattenGroupedData<TData>(table: Table<TData>): any[] {
  const result: any[] = [];
  const visibleColumns = table.getVisibleLeafColumns();
  const rows = table.getRowModel().rows;
  const grouping = table.getState().grouping;
  
  function processRow(row: Row<TData>, depth: number = 0) {
    const rowData: any = {};
    
    if (row.getIsGrouped()) {
      visibleColumns.forEach(column => {
        const cell = row.getAllCells().find(c => c.column.id === column.id);
        
        if (cell?.getIsGrouped()) {
          const context = cell.getContext();
          const value = cell.getValue();
          
          if (column.columnDef.aggregatedCell) {
            const Component = column.columnDef.aggregatedCell;
            const rendered = typeof Component === 'function' ? Component(context) : value;
            rowData[column.id] = typeof rendered === 'object' ? String(value ?? '') : (rendered ?? '');
          } else {
            rowData[column.id] = `${value ?? ''} (${row.subRows.length})`;
          }
        } else if (cell?.getIsAggregated()) {
          const context = cell.getContext();
          const value = cell.getValue();
          
          if (column.columnDef.aggregatedCell) {
            const Component = column.columnDef.aggregatedCell;
            const rendered = typeof Component === 'function' ? Component(context) : value;
            rowData[column.id] = typeof rendered === 'object' ? String(value ?? '') : (rendered ?? '');
          } else {
            rowData[column.id] = value ?? '';
          }
        } else {
          rowData[column.id] = '';
        }
      });
      
      result.push(rowData);
    }
    
    if (row.getIsExpanded() && row.subRows?.length > 0) {
      // row.subRows.forEach(subRow => processRow(subRow, depth + 1));
    }

    if (!row.getIsGrouped()) {
      visibleColumns.forEach(column => {
        // Check if this column is used for grouping
        const isGroupedColumn = grouping.includes(column.id);
        
        // For data rows, grouped columns should be empty (matching UI behavior)
        if (isGroupedColumn) {
          rowData[column.id] = '';
        } else {
          const cell = row.getAllCells().find(c => c.column.id === column.id);
          if (cell) {
            const value = cell.getValue();
            
            if (cell.getIsPlaceholder()) {
              rowData[column.id] = '';
            } else if (column.columnDef.cell && typeof column.columnDef.cell === 'function') {
              const context = cell.getContext();
              const rendered = column.columnDef.cell(context);
              
              if (typeof rendered === 'string' || typeof rendered === 'number') {
                rowData[column.id] = rendered;
              } else if (rendered && typeof rendered === 'object' && 'props' in rendered) {
                rowData[column.id] = extractTextFromReactElement(rendered);
              } else {
                rowData[column.id] = value ?? '';
              }
            } else {
              rowData[column.id] = value ?? '';
            }
          } else {
            rowData[column.id] = '';
          }
        }
      });
      
      result.push(rowData);
    }
  }
  
  rows.forEach(row => processRow(row));
  
  return result;
}

function extractTextFromReactElement(element: any): string {
  if (!element) return '';
  if (typeof element === 'string' || typeof element === 'number') return String(element);

  if (element.props?.children) {
    const children = element.props.children;
    if (typeof children === 'string' || typeof children === 'number') {
      return String(children);
    }
    if (Array.isArray(children)) {
      return children.map(extractTextFromReactElement).join(' ');
    }
    return extractTextFromReactElement(children);
  }
  
  if (element.props?.value !== undefined) {
    return String(element.props.value);
  }
  
  return '';
}

function prepareDataForCSV<TData>(table: Table<TData>): any[] {
  const grouping = table.getState().grouping;
  const visibleColumns = table.getVisibleLeafColumns();
  
  if (grouping.length === 0) {
    const rows = table.getFilteredRowModel().rows;
    return rows.map(row => {
      const rowData: any = {};
      
      visibleColumns.forEach(column => {
        const value = row.getValue(column.id);
        
        if (column.columnDef.cell && typeof column.columnDef.cell === 'function') {
          const cell = row.getAllCells().find(c => c.column.id === column.id);
          if (cell) {
            const context = cell.getContext();
            const rendered = column.columnDef.cell(context);
            
            if (typeof rendered === 'string' || typeof rendered === 'number') {
              rowData[column.id] = rendered;
            } else if (rendered && typeof rendered === 'object' && 'props' in rendered) {
              rowData[column.id] = extractTextFromReactElement(rendered);
            } else {
              rowData[column.id] = value ?? '';
            }
          } else {
            rowData[column.id] = value ?? '';
          }
        } else {
          rowData[column.id] = value ?? '';
        }
      });
      
      return rowData;
    });
  }
  
  // For grouped data, just return the flattened data without any UI elements
  return flattenGroupedData(table);
}

export async function exportToCSV<TData>(
  table: Table<TData>,
  options: ExportOptions = {}
) {
  try {
    const data = prepareDataForCSV(table);
    const transformedData = options.transform ? options.transform(data) : data;
    
    if (transformedData.length === 0) {
      throw new Error('No data to export');
    }
    
    const visibleColumns = table.getVisibleLeafColumns();
    const keys = visibleColumns.map(col => col.id);
    
    // Create header mapping - extract string from header which might be string, function, or React component
    const headers = visibleColumns.map(col => {
      const header = col.columnDef.header;
      if (typeof header === 'string') {
        return header;
      } else if (typeof header === 'function') {
        // For function headers, use the column ID as fallback
        return col.id;
      } else {
        return col.id;
      }
    });

    // Create a new array with renamed keys for proper headers
    const dataWithHeaders = transformedData.map(row => {
      const newRow: any = {};
      keys.forEach((key, index) => {
        newRow[headers[index]] = row[key];
      });
      return newRow;
    });

    const csvOptions = {
      keys: headers,
      emptyFieldValue: '',
      expandNestedObjects: false,
      useDateIso8601Format: true,
    };
    
    const csv = await json2csv(dataWithHeaders, csvOptions);
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    const defaultFilename = `data-export-${new Date().toISOString().split('T')[0]}.csv`;
    const filename = options.filename ? options.filename(transformedData) : defaultFilename;
    
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => URL.revokeObjectURL(url), 100);
    
    return { success: true, rowCount: transformedData.length };
  } catch (error) {
    console.error('Export failed:', error);
    throw error;
  }
}

export function defaultExportFilename(data: any[]): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  return `export-${timestamp}.csv`;
}
